import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Dialog, DialogOverlay, DialogPortal } from '@/shared/components/ui';
import { useMediaQuery } from '@/shared/lib';
import { cn } from '@/shared/utils';
import {
  SEARCH_CATEGORIES,
  useGlobalSearchStore,
  useVisualViewportBox,
  type SearchCategory,
} from '../model';
import { GlobalSearchInput } from './GlobalSearchInput';
import { GlobalSearchCategories } from './GlobalSearchCategories';
import { GlobalSearchResults } from './GlobalSearchResults';

const GlobalSearch = () => {
  const { t } = useTranslation();
  const { isOpen, close } = useGlobalSearchStore();
  const isTabletUp = useMediaQuery('(min-width: 1068px)');
  const viewportBox = useVisualViewportBox(isOpen && !isTabletUp);
  const inputRef = useRef<HTMLInputElement>(null);

  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<SearchCategory>(
    SEARCH_CATEGORIES.all
  );

  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setCategory(SEARCH_CATEGORIES.all);
    }
  }, [isOpen]);

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      close();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogPortal>
        <DialogOverlay className="z-80 bg-white/16 backdrop-blur-[4px]" />
        <DialogPrimitive.Content
          onOpenAutoFocus={event => {
            event.preventDefault();
            inputRef.current?.focus();
          }}
          onInteractOutside={event => {
            const target = event.target as HTMLElement | null;
            if (target?.closest('[data-radix-popper-content-wrapper]')) {
              event.preventDefault();
            }
          }}
          aria-describedby={undefined}
          style={
            viewportBox
              ? {
                  top: `calc(${viewportBox.offsetTop}px + 1.2rem)`,
                  maxHeight: `calc(${viewportBox.height}px - 2.4rem)`,
                }
              : undefined
          }
          className={cn(
            'dark fixed z-80 flex flex-col gap-[1.6rem] overflow-hidden p-[2.4rem] bg-black/72 backdrop-blur-[16px] text-primary outline-none',
            'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0 data-[state=open]:duration-200 data-[state=closed]:duration-200',
            isTabletUp
              ? 'top-[5.6rem] left-1/2 -translate-x-1/2 w-[72rem] max-w-[calc(100vw-4.8rem)] h-[48rem] max-h-[calc(100dvh-8rem)] rounded-[2.4rem]'
              : 'top-[1.2rem] left-[1.2rem] right-[1.2rem] max-h-[calc(100dvh-2.4rem)] rounded-[2rem]'
          )}>
          <DialogPrimitive.Title className="sr-only">
            {t('personalAccount.globalSearch.placeholder')}
          </DialogPrimitive.Title>

          <GlobalSearchInput
            ref={inputRef}
            value={query}
            onChange={setQuery}
            onClose={close}
          />

          <div className="flex grow min-h-0 gap-[4.8rem]">
            {isTabletUp && (
              <GlobalSearchCategories value={category} onChange={setCategory} />
            )}

            <GlobalSearchResults
              query={query}
              category={isTabletUp ? category : SEARCH_CATEGORIES.all}
              onQuickOptionSelect={setQuery}
              onResetQuery={() => setQuery('')}
              onClose={close}
            />
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
};

export { GlobalSearch };
